/**
 * Slurred notes joined to the next, as a player joins them.
 *
 * A slur asks for the notes under it to be connected. Played exactly as long as written, one
 * note stops as the next begins, and a recording's own attack then opens a gap between them
 * that no player would leave. So a note a slur carries on from is held past the start of the
 * next, by the key overlap Roberto Bresin measured in pianists' legato and wrote as the Score
 * Legato Articulation rule of the KTH performance rules, at the setting he calls natural
 * (K = 1), with the time between the two onsets in milliseconds:
 *
 *   overlap = (0.17168 - 0.0000109 * IOI) * IOI
 *
 * which is about 34 ms between notes 200 ms apart and 161 ms between notes a second apart
 * (R. Bresin, *Virtual Virtuosity*, KTH 2000, p. 9,
 * https://www.diva-portal.org/smash/get/diva2:8799/FULLTEXT01.pdf).
 *
 * A note whose next is the same pitch is not overlapped: one key or string sounding its own
 * pitch again cannot also still be sounding it, and a MIDI file's second note-on would be cut
 * by the first note's note-off.
 *
 * Which notes a slur carries on from is kept here too (`slursOf`), a bar at a time and not a
 * note at a time: a slur ends at its last note, and that note may stand in another voice of
 * its bar than the line the slur runs in, read before that line or after it. And the played
 * order is not the written one: a repeat goes back from the middle of a slur, and a
 * second-time bar is played in place of the bar a slur ran on into.
 */

import type { SpannerId } from "../document/ids";
import type { MusicEvent, Part, Score } from "../document/types";
import type { PlayedNote } from "./played";
import { spansOf } from "./spans";

/** One line of a bar: the name it keeps from bar to bar, and where each of its events begins. */
export interface BarLine {
  readonly line: string;
  readonly placed: readonly { readonly event: MusicEvent; readonly start: number }[];
}

/** Where an event stands in its bar: its line, its place in that line, the tick it begins at. */
interface Place {
  readonly line: string;
  readonly order: number;
  readonly start: number;
}

/** The slurs open in a part as it is played. */
export interface OpenSlurs {
  /**
   * Read a bar: the slurs that begin in it are open, and the ones that ended in the last are
   * over. So are the ones that do not cover it, and the lines those leave with no slur open
   * are returned.
   */
  bar(index: number, lines: readonly BarLine[]): readonly string[];
  /** Whether a slur carries on from the event of the bar read that stands there. */
  carrying(line: string, order: number, start: number): boolean;
}

/** An open slur: the line it was opened in and the bars it covers, first to last. */
interface Open {
  readonly line: string;
  readonly first: number;
  readonly last: number;
}

/** Whether one place of a bar comes before another: by order in one line, by time across two. */
function before(one: Place, other: Place): boolean {
  return one.line === other.line ? one.order < other.order : one.start < other.start;
}

/**
 * The slurs of a part, to be opened and closed as the part is played.
 *
 * A slur carries on from every note of its line from its first note up to its last, and its
 * line is the one its first note is in. Its last note need not be: a slur drawn from one
 * voice of a bar to another ends at the moment that note begins, so a note of its line
 * beginning then or later is not under it, whichever of the two voices the bar keeps first.
 * That cannot be told as the bar's voices are read one after another, so a bar is read whole
 * before any note of it is asked about. A slur open when a bar is come to is open from the
 * start of that bar.
 *
 * A slur is over as well when the played order comes to a bar it does not cover, which is how
 * a slur left by a repeat or by a skipped ending bar ends: its last note is then never
 * reached, and held open it would join every note of its line from there on. The bars are
 * indices of {@link Score.measures}. A slur whose last note is not in the part covers the bar
 * it begins in.
 */
export function slursOf(score: Score, part: Part): OpenSlurs {
  const { opens, closes, lastBar } = spansOf(score, part, ["slur"]);

  // The open slurs, and where the bar being read begins and ends them.
  const open = new Map<SpannerId, Open>();
  const begun = new Map<SpannerId, Place>();
  const ended = new Map<SpannerId, Place>();
  return {
    bar(index, lines) {
      for (const id of ended.keys()) open.delete(id);
      const left = new Set<string>();
      for (const [id, slur] of open) {
        if (index >= slur.first && index <= slur.last) continue;
        open.delete(id);
        left.add(slur.line);
      }
      const still = new Set([...open.values()].map((slur) => slur.line));
      begun.clear();
      ended.clear();
      for (const { line, placed } of lines) {
        placed.forEach(({ event, start }, order) => {
          for (const span of opens.get(event.id) ?? []) {
            // Begun here even where a repeat has come back to it still open: the notes before
            // its first are not under it on any pass.
            begun.set(span.id, { line, order, start });
            const end = lastBar.get(span.id) ?? index;
            open.set(span.id, { line, first: Math.min(index, end), last: Math.max(index, end) });
          }
          for (const span of closes.get(event.id) ?? []) ended.set(span.id, { line, order, start });
        });
      }
      return [...left].filter((line) => !still.has(line));
    },
    carrying(line, order, start) {
      const at = { line, order, start };
      for (const [id, held] of open) {
        if (held.line !== line) continue;
        const first = begun.get(id);
        const last = ended.get(id);
        if (first !== undefined && before(at, first)) continue;
        if (last === undefined || before(at, last)) return true;
      }
      return false;
    },
  };
}

/** The overlap of the rule, in seconds, for onsets `seconds` apart. */
function overlapFor(seconds: number): number {
  const ioi = seconds * 1000;
  return Math.max(0, (0.17168 - 0.0000109 * ioi) * ioi) / 1000;
}

/**
 * `notes` with every note a slur carries on from held into the next, and how many ticks each
 * was held past where it is written to end, index for index.
 *
 * `lines` names the line of music each note was read from, index for index, and `carried`
 * holds the indices of the notes a slur carries on from. The overlap is sounded during the next
 * note, so it is counted in ticks at the next note's speed, which a tempo change between the
 * two makes different from the note's own.
 */
export function joined(
  notes: readonly PlayedNote[],
  lines: readonly string[],
  carried: ReadonlySet<number>,
): { readonly notes: PlayedNote[]; readonly overlaps: number[] } {
  const overlaps = notes.map(() => 0);
  if (carried.size === 0) return { notes: [...notes], overlaps };
  const onsets = new Map<string, PlayedNote[]>();
  notes.forEach((note, index) => {
    const key = `${lines[index] ?? ""}@${note.startTicks}`;
    onsets.set(key, [...(onsets.get(key) ?? []), note]);
  });

  const held = notes.map((note, index) => {
    if (!carried.has(index) || note.duration <= 0) return note;
    const next = onsets.get(`${lines[index] ?? ""}@${note.startTicks + note.durationTicks}`);
    if (next === undefined || next.some((other) => other.midi === note.midi)) return note;
    const shortest = Math.min(...next.map((other) => other.duration));
    const overlap = Math.min(overlapFor(next[0]!.start - note.start), shortest);
    if (overlap <= 0) return note;
    // Every next note is longer than nothing, the overlap being no longer than the shortest.
    const ticks = Math.round((overlap * next[0]!.durationTicks) / next[0]!.duration);
    overlaps[index] = ticks;
    return {
      ...note,
      duration: note.duration + overlap,
      durationTicks: note.durationTicks + ticks,
    };
  });
  return { notes: held, overlaps };
}
